import { FileText } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useApuracaoSN } from '@/contexts';
import { useSegregacaoFiltros } from '../useSegregacaoFiltros';

function formatarMoeda(v: number): string {
  return v.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function gerarHtmlEspelho(params: {
  cnpj: string;
  competencia: string;
  memoriaCalculo: { label: string; formula?: string; valor: string }[];
  totais:
    | {
        Total_Saidas: number;
        Total_Cancelados: number;
        Total_NaoGeradorReceita: number;
        Total_Faturamento: number;
        Total_NaoSegregado: number;
        Total_Segregado: number;
        Total_EmRevisao: number;
      }
    | undefined;
  aliquotaEfetiva: number;
  dasARecolher: number;
  vencimento: string;
  geradoEm: string;
}): string {
  const {
    cnpj,
    competencia,
    memoriaCalculo,
    totais,
    aliquotaEfetiva,
    dasARecolher,
    vencimento,
    geradoEm,
  } = params;

  const totRows: [string, string][] = [
    ['Total de Saídas', formatarMoeda(totais?.Total_Saidas ?? 0)],
    ['Total Cancelados', formatarMoeda(totais?.Total_Cancelados ?? 0)],
    ['Faturamento', formatarMoeda(totais?.Total_Faturamento ?? 0)],
    ['Receita Segregada', formatarMoeda(totais?.Total_Segregado ?? 0)],
    ['Não Segregado', formatarMoeda(totais?.Total_NaoSegregado ?? 0)],
    ['Não Gerador de Receita', formatarMoeda(totais?.Total_NaoGeradorReceita ?? 0)],
    ['Em Revisão', formatarMoeda(totais?.Total_EmRevisao ?? 0)],
  ];

  const memoriaRows = memoriaCalculo
    .map(
      item => `
    <tr>
      <td>${item.label}</td>
      <td style="color:#666">${item.formula ?? '—'}</td>
      <td style="text-align:right;font-weight:600">${item.valor}</td>
    </tr>`
    )
    .join('');

  const totaisRows = totRows
    .map(
      ([label, valor]) => `
    <tr>
      <td>${label}</td>
      <td style="text-align:right;font-weight:600">R$ ${valor}</td>
    </tr>`
    )
    .join('');

  return `<!DOCTYPE html>
<html lang="pt-BR">
<head>
  <meta charset="UTF-8">
  <title>Espelho — Apuração Simples Nacional ${competencia}</title>
  <style>
    *{box-sizing:border-box;margin:0;padding:0}
    body{font-family:Arial,sans-serif;font-size:12px;color:#1a1a1a;background:#fff;padding:40px}
    @media print{
      body{padding:0}
      @page{margin:20mm 15mm}
      .no-print{display:none}
    }
    .page-header{border-bottom:2px solid #1a1a1a;padding-bottom:14px;margin-bottom:24px;display:flex;justify-content:space-between;align-items:flex-start}
    .page-header-title{font-size:17px;font-weight:bold}
    .page-header-sub{font-size:12px;color:#555;margin-top:5px}
    .page-header-meta{text-align:right;font-size:10px;color:#777;line-height:1.6}
    .das-box{background:#1a1a1a;color:#fff;border-radius:6px;padding:18px 24px;display:flex;justify-content:space-between;align-items:center;margin-bottom:28px}
    .das-label{font-size:11px;color:#aaa;margin-bottom:4px}
    .das-value{font-size:22px;font-weight:bold}
    .das-venc{font-size:10px;color:#888;margin-top:5px}
    .das-aliq{text-align:right}
    .das-aliq-value{font-size:20px;font-weight:bold}
    .section{margin-bottom:28px}
    .section-title{font-size:11px;font-weight:bold;letter-spacing:.8px;text-transform:uppercase;color:#444;border-bottom:1px solid #ddd;padding-bottom:5px;margin-bottom:10px}
    table{width:100%;border-collapse:collapse}
    th{background:#f4f4f4;font-size:11px;padding:7px 10px;text-align:left;border:1px solid #ddd}
    td{padding:7px 10px;border:1px solid #e8e8e8;font-size:12px;vertical-align:middle}
    .footer{margin-top:36px;border-top:1px solid #ddd;padding-top:10px;font-size:10px;color:#aaa;text-align:center}
    .print-btn{margin-bottom:24px;padding:10px 28px;font-size:13px;cursor:pointer;background:#1a1a1a;color:#fff;border:none;border-radius:5px;display:inline-flex;align-items:center;gap:8px}
  </style>
</head>
<body>
  <div class="no-print">
    <button class="print-btn" onclick="window.print()">&#x1F4BE; Imprimir / Salvar como PDF</button>
  </div>

  <div class="page-header">
    <div>
      <div class="page-header-title">Espelho de Apuração — Simples Nacional</div>
      <div class="page-header-sub">CNPJ: ${cnpj} &nbsp;|&nbsp; Competência: ${competencia}</div>
    </div>
    <div class="page-header-meta">
      Gerado em: ${geradoEm}<br>
      turboSPED
    </div>
  </div>

  <div class="das-box">
    <div>
      <div class="das-label">DAS a Recolher</div>
      <div class="das-value">R$ ${formatarMoeda(dasARecolher)}</div>
      <div class="das-venc">Vencimento: ${vencimento}</div>
    </div>
    <div class="das-aliq">
      <div class="das-label">Alíquota Efetiva</div>
      <div class="das-aliq-value">${aliquotaEfetiva.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%</div>
    </div>
  </div>

  <div class="section">
    <div class="section-title">Memória de Cálculo</div>
    <table>
      <thead>
        <tr>
          <th>Item</th>
          <th>Fórmula</th>
          <th style="text-align:right">Valor</th>
        </tr>
      </thead>
      <tbody>${memoriaRows}</tbody>
    </table>
  </div>

  <div class="section">
    <div class="section-title">Totais Gerais</div>
    <table>
      <thead>
        <tr>
          <th>Descrição</th>
          <th style="text-align:right">Valor (R$)</th>
        </tr>
      </thead>
      <tbody>${totaisRows}</tbody>
    </table>
  </div>

  <div class="footer">
    Documento gerado automaticamente pelo turboSPED &mdash; ${geradoEm}
  </div>
</body>
</html>`;
}

export function ExportarEspelhoButton() {
  const { dados, segregacao } = useApuracaoSN();
  const calcularPayload = useSegregacaoFiltros();

  if (!segregacao) return null;

  const handleExportarEspelho = () => {
    const totais = segregacao.TOT?.[0];
    const geradoEm = new Date().toLocaleString('pt-BR');

    const html = gerarHtmlEspelho({
      cnpj: calcularPayload?.CnpjContribuinte ?? '—',
      competencia: dados.competencia,
      memoriaCalculo: dados.memoriaCalculo,
      totais,
      aliquotaEfetiva: dados.aliquotaEfetiva,
      dasARecolher: dados.dasARecolher,
      vencimento: dados.vencimento,
      geradoEm,
    });

    const win = window.open('', '_blank', 'width=900,height=720,scrollbars=yes');
    if (!win) {
      return;
    }
    win.document.write(html);
    win.document.close();
  };

  return (
    <Button variant="outline" size="sm" className="gap-2" onClick={handleExportarEspelho}>
      <FileText className="h-4 w-4" />
      Exportar Espelho
    </Button>
  );
}
